// src/hooks/useWeatherData.js
import { useState, useEffect, useMemo } from 'react';
import * as weatherUtils from '../utils/weatherCalculations';

export const useWeatherData = (timeRange = '24h') => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [units, setUnits] = useState({
    temperature: 'C',
    pressure: 'hPa'
  });

  const getApiUrl = (range) => {
    const baseUrl = 'https://api.thingspeak.com/channels/2694097/feeds.json';

    // Handle custom range
    if (typeof range === 'object' && range.start && range.end) {
      const start = new Date(range.start).toISOString();
      const end = new Date(range.end).toISOString();
      return `${baseUrl}?start=${start}&end=${end}`;
    }

    // Handle preset ranges
    const results = getResultsCount(range);
    return `${baseUrl}?results=${results}`;
  };

  const getResultsCount = (range) => {
    switch(range) {
      case '1h': return 60;
      case '3h': return 180;
      case '6h': return 360;
      case '12h': return 720;
      case '24h': return 1440;
      case '2d': return 2880;
      case '7d': return 10080;
      case '30d': return 43200;
      default: return 60;
    }
  };

  const validatePressure = (pressure) => {
    // Typical sea level pressure range: 870 hPa to 1090 hPa
    const minPressure = 870;
    const maxPressure = 1090;
    const parsedPressure = parseFloat(pressure) / 100;
    
    return parsedPressure >= minPressure && parsedPressure <= maxPressure
      ? parsedPressure
      : null;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const apiUrl = getApiUrl(timeRange);
        const response = await fetch(apiUrl);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const jsonData = await response.json();
        
        // Transform the data
        const transformedData = jsonData.feeds
          .map(feed => {
            const temperature = parseFloat(feed.field1);
            const humidity = parseFloat(feed.field2);
            const pressure = validatePressure(feed.field3);

            if (isNaN(temperature) || isNaN(humidity) || pressure === null) {
              return null;
            }

            return {
              timestamp: new Date(feed.created_at),
              temperature,
              humidity,
              pressure,
              dewPoint: weatherUtils.calculateDewPoint(temperature, humidity),
              heatIndex: weatherUtils.calculateHeatIndex(temperature, humidity),
              wetBulb: weatherUtils.calculateWetBulb(temperature, humidity, pressure),
              absoluteHumidity: weatherUtils.calculateAbsoluteHumidity(temperature, humidity),
              airDensity: weatherUtils.calculateAirDensity(temperature, pressure, humidity)
            };
          })
          .filter(reading => reading !== null)
          .sort((a, b) => a.timestamp - b.timestamp); // Ensure chronological order
        
        setData(transformedData);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
        console.error('Error fetching weather data:', err);
      }
    };

    fetchData();
    // Only set up interval for auto-refresh if using a preset range
    if (typeof timeRange !== 'object') {
      const interval = setInterval(fetchData, 60000); // Update every minute
      return () => clearInterval(interval);
    }
  }, [timeRange]);

  // Convert values based on selected units
  const convertValue = (value, type) => {
    if (value === null || value === undefined || isNaN(value)) {
      return null;
    }
    
    if (type === 'temperature' && units.temperature === 'F') {
      return weatherUtils.celsiusToFahrenheit(value);
    }
    if (type === 'pressure' && units.pressure === 'inHg') {
      return weatherUtils.hPaToInHg(value);
    }
    return value;
  };

  // Process data for statistics and analysis
  const processedData = useMemo(() => {
    if (!data.length) return null;

    const getStats = (values) => {
      const filteredValues = values.filter(v => v !== null && !isNaN(v));
      if (filteredValues.length === 0) return null;

      const sorted = [...filteredValues].sort((a, b) => a - b);
      const sum = sorted.reduce((a, b) => a + b, 0);
      const mean = sum / sorted.length;

      return {
        min: sorted[0],
        max: sorted[sorted.length - 1],
        mean: mean,
        median: sorted.length % 2 === 0 
          ? (sorted[sorted.length/2 - 1] + sorted[sorted.length/2]) / 2
          : sorted[Math.floor(sorted.length/2)],
        stdDev: Math.sqrt(
          sorted.reduce((acc, val) => acc + Math.pow(val - mean, 2), 0) / sorted.length
        )
      };
    };

    // Get the latest reading
    const latest = data[data.length - 1];
    
    // Get the previous reading for trend calculation
    const previous = data[data.length - 2] || latest;

    // Calculate time range for the dataset
    const timeRange = {
      start: data[0].timestamp,
      end: data[data.length - 1].timestamp,
      duration: data[data.length - 1].timestamp - data[0].timestamp
    };

    return {
      temperature: {
        current: latest.temperature,
        stats: getStats(data.map(d => d.temperature)),
        trend: latest.temperature - previous.temperature
      },
      humidity: {
        current: latest.humidity,
        stats: getStats(data.map(d => d.humidity)),
        trend: latest.humidity - previous.humidity
      },
      pressure: {
        current: latest.pressure,
        stats: getStats(data.map(d => d.pressure)),
        trend: latest.pressure - previous.pressure
      },
      dewPoint: {
        current: latest.dewPoint,
        stats: getStats(data.map(d => d.dewPoint))
      },
      heatIndex: {
        current: latest.heatIndex,
        stats: getStats(data.map(d => d.heatIndex))
      },
      wetBulb: {
        current: latest.wetBulb,
        stats: getStats(data.map(d => d.wetBulb))
      },
      absoluteHumidity: {
        current: latest.absoluteHumidity,
        stats: getStats(data.map(d => d.absoluteHumidity))
      },
      airDensity: {
        current: latest.airDensity,
        stats: getStats(data.map(d => d.airDensity))
      },
      comfortLevel: weatherUtils.getComfortLevel(
        latest.temperature,
        latest.humidity
      ),
      timeRange,
      dataPoints: data.length
    };
  }, [data]);

  return {
    data,
    processedData,
    loading,
    error,
    units,
    setUnits,
    convertValue
  };
};